import { useEffect } from 'react';

import { useSubscriptionPortalMutation } from '@/store/subscriptions/subscriptions.store';

/**
 * Opens Stripe's billing portal — payment methods, invoices, receipts — and reports the
 * state whichever button offers it needs to show.
 *
 * The session is created for the signed-in person: the server reads the Stripe customer
 * from the JWT, so any service the person belongs to satisfies the `X-Service-Id` header
 * without narrowing what the portal covers.
 *
 * Leaving for Stripe is a redirect rather than an unmount, so the button that started it is
 * still mounted — and its mutation still settled — when the browser's back button restores
 * the page from its cache. That is what left the button reading "Preparing…" and refusing
 * every further click, so the mutation is reset on `pageshow`, which fires on a restore as
 * well as on a fresh load.
 */
export const useBillingPortal = (serviceId?: string) => {
  const portal = useSubscriptionPortalMutation();
  const { reset } = portal;

  useEffect(() => {
    const onPageShow = () => reset();
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [reset]);

  // `isSuccess` counts as redirecting too: the URL has resolved and the browser is leaving.
  const isRedirecting = portal.isPending || portal.isSuccess;

  return {
    isRedirecting,
    isError: portal.isError,
    error: portal.error,
    /** There is nothing to name in the header until the person's services have loaded. */
    canOpen: !!serviceId && !isRedirecting,
    open: () => {
      if (!serviceId || isRedirecting) return;
      portal.mutate({ serviceId }, { onSuccess: (url) => window.location.assign(url) });
    },
  };
};
